import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { T } from "../../constants";
import { useUi } from "../../ui";

/*
 * The one action a booking row offers, and the note under its status badge.
 *
 * Both booking tables render the same ladder, so it lives here rather than
 * being kept in step by hand in two files.
 *
 * A row on a chargeback guest card never reaches the payment rungs: the hotel
 * invoices it, so there is nothing to take at the desk and the row goes
 * straight to allocating a ticket.
 */

const useStyles = makeStyles({
  pendingBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    border: `1px solid ${T.warningSolid}`,
    background: T.warningTint,
    color: T.warningFg,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    padding: "5px 10px",
    whiteSpace: "nowrap",
  },
  muted: { color: T.mutedFg },
});

/** Whether the desk can ever be asked to collect for this row. */
export function billsAtDesk(booking) {
  return booking?.paymentRequired !== false;
}

/** The note under the status badge, or null when there is nothing to say. */
export function statusNote(booking) {
  if (!billsAtDesk(booking)) return "Hotel invoiced";
  if (booking?.pendingCharge) return "Payment pending";
  if (!booking?.paid) return "Awaiting payment";
  if (!booking?.hasTicket) return "Awaiting allocation";

  return null;
}

export default function BookingRowAction({ booking, onTakePayment, onAssign, onValidate }) {
  const s = useStyles();
  const ui = useUi();

  const stop = (handler) => (event) => {
    event.stopPropagation();
    handler(booking);
  };

  if (billsAtDesk(booking)) {
    if (booking.pendingCharge) {
      return <span className={s.pendingBadge}>Pending</span>;
    }

    if (!booking.paid) {
      return (
        <button onClick={stop(onTakePayment)} className={ui.primaryBtn}>
          Take payment
        </button>
      );
    }
  }

  if (!booking.hasTicket) {
    return (
      <button onClick={stop(onAssign)} className={ui.primaryBtn}>
        Assign ticket
      </button>
    );
  }

  if (booking.status === "allocated" || booking.status === "flagged") {
    return (
      <button onClick={stop(onValidate)} className={ui.ghostBtn}>
        Validate
      </button>
    );
  }

  return <span className={s.muted}>-</span>;
}
